import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { tokenStorage } from '@/auth/tokenStorage';
import type { StoredSession } from '@/auth/tokenStorage';
import type { AuthenticateResultModel } from '@/api/types/account';
import { tenantStore } from '@/tenant/tenantStore';
import { env } from '@/config/env';
import { toApiError } from './abpError';

// Called when the session can no longer be recovered (refresh failed / no token
// on a 401). Registered by the auth layer so the client stays UI-agnostic.
let onAuthFailure: () => void = () => {};
export function setOnAuthFailure(cb: () => void) {
  onAuthFailure = cb;
}

function currentTenantHeader(): string | null {
  // Pre-login tenant hint (dev/host). Post-login the backend derives tenant from
  // the token claim, so this is harmless once authenticated.
  return tenantStore.effective() || null;
}

function readCookie(name: string): string | null {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const m = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

const SAFE_METHODS = new Set(['get', 'head', 'options', 'trace']);

export const http: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl || undefined,
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.accessToken();
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  const tenant = currentTenantHeader();
  if (tenant) config.headers.set('__tenant', tenant);
  // ABP auto-validates antiforgery on unsafe methods: echo the XSRF-TOKEN cookie
  // as the RequestVerificationToken header (the abp.js proxy does this for Razor pages).
  if (!SAFE_METHODS.has((config.method || 'get').toLowerCase())) {
    const xsrf = readCookie('XSRF-TOKEN');
    if (xsrf) config.headers.set('RequestVerificationToken', xsrf);
  }
  return config;
});

// --- Single-flight refresh ---------------------------------------------------
// A bare instance with no interceptors, so refreshing never recurses.
const bare = axios.create({ baseURL: env.apiBaseUrl || undefined });
let refreshInFlight: Promise<string | null> | null = null;

function persistFromResult(r: AuthenticateResultModel): StoredSession | null {
  if (!r.accessToken || !r.refreshToken) return null;
  const session: StoredSession = {
    accessToken: r.accessToken,
    refreshToken: r.refreshToken,
    tokenType: r.tokenType || 'Bearer',
    expiresAt: Date.now() + Math.max(0, r.expireInSeconds - 30) * 1000,
  };
  tokenStorage.save(session);
  return session;
}

// --- Antiforgery refresh (single-flight) -------------------------------------
// ABP re-issues the XSRF-TOKEN cookie on this GET. The antiforgery token can go
// stale during a long-lived SPA session, after which every unsafe request fails
// with an empty-body 400 until the cookie is refreshed. We refresh it lazily, only
// when a 400 that looks like an antiforgery failure is seen.
let xsrfRefreshInFlight: Promise<void> | null = null;
function refreshAntiforgery(): Promise<void> {
  xsrfRefreshInFlight ??= (() => {
    const headers: Record<string, string> = {};
    const token = tokenStorage.accessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const tenant = currentTenantHeader();
    if (tenant) headers['__tenant'] = tenant;
    return bare
      .get('/api/abp/application-configuration', { headers })
      .then(() => undefined)
      .catch(() => undefined)
      .finally(() => {
        xsrfRefreshInFlight = null;
      });
  })();
  return xsrfRefreshInFlight;
}

async function refreshAccessToken(): Promise<string | null> {
  const session = tokenStorage.load();
  if (!session?.refreshToken) return null;

  const headers: Record<string, string> = {};
  const tenant = currentTenantHeader();
  if (tenant) headers['__tenant'] = tenant;

  try {
    const { data } = await bare.post<AuthenticateResultModel>(
      '/api/app/account-session/refresh-token',
      { refreshToken: session.refreshToken },
      { headers },
    );
    if (data.error || !data.accessToken) return null;
    return persistFromResult(data)?.accessToken ?? null;
  } catch {
    return null;
  }
}

http.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean; _xsrfRetried?: boolean })
      | undefined;
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;

    // Stale antiforgery token → ASP.NET returns an empty-body 400 on unsafe
    // methods. Refresh the XSRF-TOKEN cookie once and retry (a real validation
    // 400 carries a JSON body, so it won't match and falls through).
    const method = (original?.method || 'get').toLowerCase();
    const body = axios.isAxiosError(error) ? error.response?.data : undefined;
    const emptyBody = body == null || (typeof body === 'string' && body.trim() === '');
    if (status === 400 && original && !original._xsrfRetried && !SAFE_METHODS.has(method) && emptyBody) {
      original._xsrfRetried = true;
      await refreshAntiforgery();
      const xsrf = readCookie('XSRF-TOKEN');
      if (xsrf) original.headers.set('RequestVerificationToken', xsrf);
      return http(original);
    }

    if (status === 401 && original && !original._retried && tokenStorage.load()?.refreshToken) {
      original._retried = true;
      refreshInFlight ??= refreshAccessToken().finally(() => {
        refreshInFlight = null;
      });
      const newToken = await refreshInFlight;
      if (newToken) {
        original.headers.set('Authorization', `Bearer ${newToken}`);
        return http(original);
      }
      tokenStorage.clear();
      onAuthFailure();
    }

    return Promise.reject(toApiError(error));
  },
);

// Save the result of a fresh login so the client can start authorizing requests.
export { persistFromResult };
